import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, CreditCard, Wallet, Receipt, ClipboardList, ShieldCheck, BookOpenCheck, BarChart4, CheckCircle2 } from "lucide-react";
import { SectionLabel, HighlightText, ToolPill, SectionHeading } from "@/components/Bits";
import ComparisonTable from "@/components/ComparisonTable";
import IndustriesGrid from "@/components/IndustriesGrid";
import TeamCarousel from "@/components/TeamCarousel";
import FAQAccordion from "@/components/FAQAccordion";
import FinalCTA from "@/components/FinalCTA";
import { COMMON_FAQ, INDUSTRIES_BOOK } from "@/lib/content";

const SERVICES = [
  { icon: CreditCard, title: "Accounts Payable & Receivable", desc: "Managing invoices, payments and collections on time, every time." },
  { icon: BookOpenCheck, title: "Reconciliations", desc: "Bank, credit card and merchant accounts reconciled monthly without surprises." },
  { icon: FileText, title: "Financial Reporting", desc: "P&L, balance sheets, and cashflow reports prepared the way you need them." },
  { icon: Wallet, title: "Payroll Processing", desc: "End-to-end payroll runs on Gusto, ADP or Rippling fully compliant." },
  { icon: Receipt, title: "Expense Management", desc: "Receipts, categorization, and corporate card reconciliation handled cleanly." },
  { icon: ClipboardList, title: "Tax Prep Support", desc: "Books closed, schedules prepared, and CPA-ready handover every quarter." },
  { icon: BarChart4, title: "Cashflow Tracking", desc: "Daily/weekly visibility on inflows, outflows, runway and forecasts." },
  { icon: ShieldCheck, title: "Audit Readiness", desc: "Documentation, trails, and clean ledgers. Always investor and auditor ready." },
];

const TOOLS = [
  { cat: "Accounting & Bookkeeping", items: ["QuickBooks", "Xero", "FreshBooks", "Wave", "Sage"] },
  { cat: "Payments & Invoicing", items: ["Bill.com", "Stripe", "PayPal", "Square"] },
  { cat: "Payroll", items: ["Gusto", "ADP", "Rippling"] },
  { cat: "Expense Management", items: ["Expensify", "Ramp", "Brex", "Divvy"] },
  { cat: "Reporting & Collaboration", items: ["Google Sheets", "Microsoft Excel", "Notion", "Slack"] },
];

const TEAM = [
  { initials: "RM", name: "Rahul M.", role: "Bookkeeping · 5 yrs", bio: "Specializes in agencies and professional services.", tools: ["FreshBooks", "Bill.com", "Gusto", "Google Sheets"] },
  { initials: "SP", name: "Sneha P.", role: "Bookkeeping · 6 yrs", bio: "Ecommerce multi-channel reconciliations and inventory cost.", tools: ["QuickBooks", "Stripe", "Xero", "A2X"] },
  { initials: "AK", name: "Aman K.", role: "Bookkeeping · 4 yrs", bio: "SaaS revenue recognition, MRR tracking and investor reporting.", tools: ["Xero", "Stripe", "ChartMogul", "Notion"] },
];

export default function BookKeeping() {
  return (
    <>
      <Helmet>
        <title>Hire Expert Bookkeeping Services | Aidify Virtual Assistance</title>
        <meta name="description" content="Looking for reliable Bookkeeping Services? Aidify offers expert Virtual Bookkeeping Assistant support, Online Bookkeeping Services, and Bookkeeping and Payroll Services to keep your finances on track. Get Started Today!" />
      </Helmet>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>Bookkeeping Professionals</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[84px] leading-[0.95]">
              Your Books.
              <br />
              <span className="font-serif-italic font-normal">Always Clean.</span>
              <br />
              <HighlightText><span className="font-serif-italic font-normal">Always Accountable.</span></HighlightText>
            </h1>
            <p className="mt-7 max-w-xl text-forest-400 text-[16px] leading-relaxed">
              Get a dedicated offshore bookkeeper backed by a US partner who takes full local accountability. Accurate, consistent financials with zero stress and no freelancer headaches.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary">Get a Bookkeeper Today <ArrowRight size={16} /></Link>
              <span className="text-xs text-forest-400/80">Available within 2 hours · No long contracts · No Upwork-style drama</span>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="aidify-card-dark p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-cream-100/60 mb-4">Live Snapshot</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">2h</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">avg. match</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">100%</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">US Oversight</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">6+</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Yrs avg exp</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">15+</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Industries</div></div>
              </div>
              <div className="mt-4 text-xs text-cream-100/60">★★★★★ Bench ready · Pre-vetted</div>
            </div>
          </div>
        </div>

        {/* Tools strip */}
        <div className="mt-14 border-y border-forest-500/10 py-5 overflow-hidden">
          <div className="flex items-center gap-8 whitespace-nowrap">
            <div className="flex gap-8 marquee-track">
              {[...["Tax Prep", "Payroll", "Financial Reporting", "AP/AR", "Reconciliation"], ...["Tax Prep", "Payroll", "Financial Reporting", "AP/AR", "Reconciliation"]].map((t, i) => (
                <span key={i} className="font-display font-bold text-2xl text-forest-400/40 uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="What they do"
          title="Everything your"
          italic="books need."
          description="Our bookkeeping professionals handle the full scope of financial operations. So your books stay accurate, compliant and insightful while you focus on growing your business."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => (
            <div key={i} className={`aidify-card p-5 ${i % 4 === 1 ? "aidify-card-sage" : ""}`} data-reveal>
              <div className="w-9 h-9 rounded-lg bg-sage-200 flex items-center justify-center mb-4">
                <s.icon size={16} className="text-forest-600" />
              </div>
              <h4 className="font-display font-bold text-forest-600 text-base">{s.title}</h4>
              <p className="text-xs text-forest-400 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 aidify-card-sage p-5 flex items-center gap-3">
          <CheckCircle2 size={18} className="text-forest-600" />
          <p className="font-display font-bold text-forest-600 text-lg">Your books shouldn't keep you up at night.</p>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Tools they use"
          title="Fluent in"
          italic="the tools."
          description="No learning curve. No setup delays. Our bookkeepers are pre-trained on the platforms your business depends on."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOLS.map((cat, i) => (
            <div key={i} className="aidify-card p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-forest-400 mb-3">{cat.cat}</div>
              <div className="flex flex-wrap gap-1.5">{cat.items.map((t, j) => <ToolPill key={j}>{t}</ToolPill>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Industries we serve"
          title="We know"
          italic="your industry."
          description="Our bookkeepers are matched based on your specific sector, so they understand the nuances and can contribute effectively from day one."
        />
        <IndustriesGrid featured={INDUSTRIES_BOOK.featured} others={INDUSTRIES_BOOK.others} />
      </section>

      {/* COMPARISON */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The honest comparison"
          title="The smarter choice"
          italic="is clear."
          description="We will let the numbers do the talking. Hover any row to see the contrast clearly."
        />
        <ComparisonTable />
      </section>

      {/* TEAM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Meet your bookkeepers"
          title="Real"
          italic="professionals."
          description="Every bookkeeper on our bench has been vetted, tested and trained to US business standards. Here is a glimpse of who you could be working with."
        />
        <TeamCarousel members={TEAM} />
      </section>

      {/* FAQ */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Frequently asked"
          title="Questions,"
          italic="Clearly answered."
          description="Everything you need to know before hiring your first Aidify bookkeeper."
        />
        <FAQAccordion items={COMMON_FAQ} />
      </section>

      <FinalCTA />
    </>
  );
}
